import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, Zap, CreditCard, User as UserIcon, Sparkles, ArrowRight, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import TeamPanel from "@/components/TeamPanel";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const PLAN_LABELS = { free: "Free", pro: "BizForge Core", business: "BizForge Command" };

const INDUSTRIES = [
  "Photography & Video", "Design & Creative", "Web / App Development", "Marketing & Advertising Agency",
  "Consulting", "Coaching & Training", "E-commerce & Retail", "SaaS & Technology", "Event Planning",
  "Real Estate", "Construction & Trades", "Home Services (cleaning, landscaping)", "Health & Wellness",
  "Beauty & Salon", "Fitness & Personal Training", "Food, Catering & Hospitality", "Legal Services",
  "Accounting & Bookkeeping", "Education & Tutoring", "Writing & Content", "Music & Entertainment",
  "Interior Design", "Architecture", "Automotive", "IT & Managed Services", "Logistics & Transportation",
  "Manufacturing", "Nonprofit", "Professional Services", "Freelance / Creative", "Other",
];
const SIZES = ["Just me", "1-10", "11-50"];

function BusinessProfileDialog({ open, onOpenChange, business, onSaved }) {
  const EMPTY = { name: "", industry: "Consulting", size: "1-10", offering: "", target_customers: "", differentiator: "", goals: "", home_airport: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({
      name: business?.name || "", industry: business?.industry || "Consulting", size: business?.size || "1-10",
      offering: business?.offering || "", target_customers: business?.target_customers || "",
      differentiator: business?.differentiator || "", goals: business?.goals || "", home_airport: business?.home_airport || "",
    });
  }, [open, business]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Business name is required."); return; }
    setSaving(true);
    try {
      await api.put("/business", form);
      toast.success("Business profile updated — your AI is now up to date.");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="edit-business-dialog">
        <DialogHeader>
          <DialogTitle>Edit business profile</DialogTitle>
          <DialogDescription>Keep this current — your AI co-pilot uses it to tailor insights, outreach, and trip planning.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Business name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-biz-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                <SelectTrigger data-testid="edit-biz-industry-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Team size</Label>
              <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
                <SelectTrigger data-testid="edit-biz-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>What does your business offer?</Label><Textarea value={form.offering} onChange={(e) => setForm({ ...form, offering: e.target.value })} rows={3} data-testid="edit-biz-offering-input" placeholder="Summarize your products or services." /></div>
          <div className="space-y-1.5"><Label>Who are your ideal clients or customers?</Label><Textarea value={form.target_customers} onChange={(e) => setForm({ ...form, target_customers: e.target.value })} rows={2} data-testid="edit-biz-target-input" /></div>
          <div className="space-y-1.5"><Label>What sets you apart? Your specialties or edge</Label><Textarea value={form.differentiator} onChange={(e) => setForm({ ...form, differentiator: e.target.value })} rows={2} data-testid="edit-biz-differentiator-input" /></div>
          <div className="space-y-1.5"><Label>Your main goal right now</Label><Textarea value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} rows={2} data-testid="edit-biz-goals-input" /></div>
          <div className="space-y-1.5"><Label>Home airport / city <span className="font-normal text-muted-foreground">(helps the AI plan your travel)</span></Label><Input value={form.home_airport} onChange={(e) => setForm({ ...form, home_airport: e.target.value })} placeholder="e.g. Austin or AUS" data-testid="edit-biz-home-airport-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving || !form.name.trim()} data-testid="save-business-button">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save profile</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TIERS = [
  { id: "free", name: "Free", price: 0, credits: 20, perks: ["Core modules", "20 AI credits / mo", "1 user"] },
  { id: "pro", name: "BizForge Core", price: 15, credits: 500, perks: ["Everything in Free", "500 AI credits / mo", "2 team seats", "Full analytics"], highlight: true },
  { id: "business", name: "BizForge Command", price: 30, credits: 5000, perks: ["Everything in BizForge Core", "5000 AI credits / mo", "Up to 5 team seats", "Client portal for file deliverables"] },
];

// Prominent upgrade nudge for Free, subtle one for Pro, nothing for Business.
function PlanCTA({ plan, onUpgrade, loadingPlan }) {
  if (plan === "free") {
    return (
      <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/10 to-transparent p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="cta-upgrade-pro">
        <div>
          <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /><span className="text-xs font-semibold uppercase tracking-wider text-accent">Grow faster</span></div>
          <h3 className="mt-2 font-display text-xl font-bold">Upgrade to BizForge Core</h3>
          <p className="mt-1 max-w-lg text-sm text-muted-foreground">Get 500 AI credits, full analytics, and a 2nd seat for your partner — everything you need to run and scale your business.</p>
        </div>
        <Button size="lg" className="shrink-0" onClick={() => onUpgrade("pro")} disabled={!!loadingPlan} data-testid="cta-pro-button">
          {loadingPlan === "pro" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />} Upgrade to BizForge Core — $15/mo
        </Button>
      </div>
    );
  }
  if (plan === "pro") {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-5 py-3.5" data-testid="cta-upgrade-business">
        <p className="text-sm text-muted-foreground">Need more seats or credits? <span className="font-medium text-foreground">BizForge Command</span> adds up to 5 team seats.</p>
        <Button variant="ghost" size="sm" className="shrink-0 text-accent hover:text-accent" onClick={() => onUpgrade("business")} disabled={!!loadingPlan} data-testid="cta-business-button">
          Explore BizForge Command <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    );
  }
  return null;
}

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState("");
  const [polling, setPolling] = useState(false);
  const [editBiz, setEditBiz] = useState(false);
  const isOwner = user?.role === "owner";

  useEffect(() => {
    const status = params.get("checkout");
    const sessionId = params.get("session_id");
    if (status === "success" && sessionId) {
      pollStatus(sessionId, 0);
    } else if (status === "cancel") {
      toast.info("Checkout canceled.");
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollStatus = async (sessionId, attempt) => {
    if (attempt === 0) setPolling(true);
    if (attempt > 6) { setPolling(false); setParams({}); toast.error("Payment status timed out. Refresh in a moment."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false); setParams({});
        toast.success(`Upgraded to ${PLAN_LABELS[data.plan] || data.plan}!`);
        refreshUser();
        return;
      }
      if (data.status === "expired") { setPolling(false); setParams({}); toast.error("Checkout expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    }
  };

  const upgrade = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoadingPlan("");
    }
  };

  const used = user?.ai_credits_used || 0;
  const limit = user?.ai_credits_limit || 20;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage your profile, team, and plan." testId="settings-header" />

      {polling && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-4" data-testid="checkout-polling">
          <Loader2 className="h-5 w-5 animate-spin text-accent" />
          <span className="text-sm">Confirming your payment…</span>
        </div>
      )}

      {isOwner && <PlanCTA plan={user?.plan} onUpgrade={upgrade} loadingPlan={loadingPlan} />}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid="settings-profile">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground"><UserIcon className="h-4 w-4" /><span className="text-sm font-semibold uppercase tracking-wider">Profile</span></div>
            {isOwner && <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-foreground" onClick={() => setEditBiz(true)} data-testid="edit-business-button"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>}
          </div>
          <div className="flex items-center gap-2">
            <p className="font-display text-lg font-bold">{user?.name}</p>
            <Badge variant="secondary" className="capitalize" data-testid="your-role-badge">{user?.role}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Business</span><span className="font-medium">{user?.business?.name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Industry</span><span className="font-medium">{user?.business?.industry || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Team size</span><span className="font-medium">{user?.business?.size || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Home airport</span><span className="font-medium">{user?.business?.home_airport || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Sign-in</span><span className="font-medium capitalize">{user?.auth_provider}</span></div>
          </div>
          {(user?.business?.offering || user?.business?.target_customers || user?.business?.differentiator) && (
            <div className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
              {user?.business?.offering && <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Offering</p><p className="mt-0.5 text-foreground/90">{user.business.offering}</p></div>}
              {user?.business?.target_customers && <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ideal clients</p><p className="mt-0.5 text-foreground/90">{user.business.target_customers}</p></div>}
              {user?.business?.differentiator && <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">What sets us apart</p><p className="mt-0.5 text-foreground/90">{user.business.differentiator}</p></div>}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 lg:col-span-2" data-testid="settings-usage">
          <div className="mb-4 flex items-center gap-2 text-muted-foreground"><Sparkles className="h-4 w-4" /><span className="text-sm font-semibold uppercase tracking-wider">AI Usage</span></div>
          <div className="flex items-center justify-between">
            <p className="font-display text-2xl font-bold">{used} <span className="text-base font-normal text-muted-foreground">/ {limit} credits</span></p>
            <Badge className="capitalize" data-testid="current-plan-badge">{PLAN_LABELS[user?.plan] || user?.plan}</Badge>
          </div>
          <Progress value={Math.min(100, (used / limit) * 100)} className="mt-3 h-2" />
          <p className="mt-2 text-sm text-muted-foreground">Credits are shared across your team and used by the AI Hub, Content Studio, and AI actions across the app.</p>
        </div>
      </div>

      <TeamPanel onUpgrade={upgrade} />

      {isOwner && (
        <div className="mt-8">
          <div className="mb-4 flex items-center gap-2 text-muted-foreground"><CreditCard className="h-4 w-4" /><span className="text-sm font-semibold uppercase tracking-wider">Billing & Plans</span></div>
          <div className="grid gap-4 md:grid-cols-3">
            {TIERS.map((t) => {
              const current = user?.plan === t.id;
              return (
                <div key={t.id} className={`rounded-2xl border p-6 transition-all duration-200 ${t.highlight ? "border-accent bg-accent/5" : "border-border bg-card"}`} data-testid={`plan-card-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold">{t.name}</h3>
                    {current && <Badge variant="secondary">Current</Badge>}
                    {!current && t.highlight && <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">Popular</span>}
                  </div>
                  <p className="mt-3"><span className="font-display text-3xl font-bold">${t.price}</span><span className="text-muted-foreground">/mo</span></p>
                  <ul className="mt-5 space-y-2.5">
                    {t.perks.map((p) => <li key={p} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-accent" /> {p}</li>)}
                  </ul>
                  {t.id === "free" ? (
                    <Button variant="outline" className="mt-6 w-full" disabled data-testid="plan-free-button">{current ? "Current plan" : "Free"}</Button>
                  ) : current ? (
                    <Button variant="outline" className="mt-6 w-full" disabled data-testid={`plan-${t.id}-current`}>Current plan</Button>
                  ) : (
                    <Button className="mt-6 w-full" onClick={() => upgrade(t.id)} disabled={!!loadingPlan} data-testid={`upgrade-${t.id}-button`}>
                      {loadingPlan === t.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />} Upgrade to {t.name}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Payments run in Stripe test mode. Use card 4242 4242 4242 4242, any future date and CVC.</p>
        </div>
      )}

      <BusinessProfileDialog open={editBiz} onOpenChange={setEditBiz} business={user?.business} onSaved={refreshUser} />
    </div>
  );
}
